import { useState } from "react";
import { PASCO_COLOURS, SEALANT_PRODUCTS } from "@/lib/pascoColours";
import { Check, X, Plus, Search, Droplet } from "lucide-react";

const slug = (s) => (s || "").replace(/\s+/g, "-").toLowerCase();

const Swatch = ({ hex, px = 20 }) => (
  <span className="inline-block rounded-full border border-slate-300 shrink-0"
    style={{ background: hex, width: px, height: px }} />
);

function ColourMultiSelect({ product, selected, onToggle }) {
  const [q, setQ] = useState("");
  const list = PASCO_COLOURS[product] || [];
  const matches = q ? list.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())) : list;
  const isSel = (name) => selected.some((s) => s.name === name);
  return (
    <div>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid={`colour-search-${slug(product)}`} value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search colours (e.g. gre, mat, bla)…"
          className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-navy" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-2 max-h-52 overflow-y-auto">
        {matches.map((c) => (
          <button key={c.name} type="button" data-testid={`colour-toggle-${slug(product)}-${slug(c.name)}`}
            onClick={() => onToggle(c)}
            className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-sm text-left transition-colors ${isSel(c.name) ? "border-navy bg-navy/5" : "border-slate-200 hover:bg-slate-50"}`}>
            <Swatch hex={c.hex} />
            <span className="flex-1 truncate text-navy">{c.name}</span>
            {isSel(c.name) && <Check className="w-4 h-4 text-navy shrink-0" />}
          </button>
        ))}
        {matches.length === 0 && <p data-testid="no-colour-found" className="col-span-full text-sm text-slate-400 px-1 py-2">No matching colour.</p>}
      </div>
    </div>
  );
}

// Multi-product / multi-colour picker. value = [{ product, colours: [{name, hex}] }]
export default function MaterialsPicker({ value, onChange }) {
  const materials = value || [];
  const [addOpen, setAddOpen] = useState(false);
  const usedProducts = materials.map((m) => m.product);
  const available = SEALANT_PRODUCTS.filter((p) => !usedProducts.includes(p));

  const addProduct = (p) => { onChange([...materials, { product: p, colours: [] }]); setAddOpen(false); };
  const removeProduct = (i) => onChange(materials.filter((_, x) => x !== i));
  const toggleColour = (i, c) => {
    const m = materials[i];
    const has = m.colours.some((x) => x.name === c.name);
    const colours = has ? m.colours.filter((x) => x.name !== c.name) : [...m.colours, { name: c.name, hex: c.hex }];
    const next = [...materials]; next[i] = { ...m, colours }; onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="materials-picker">
      {materials.map((m, i) => (
        <div key={m.product} data-testid={`material-card-${slug(m.product)}`} className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-navy flex items-center gap-2"><Droplet className="w-4 h-4" /> {m.product}</span>
            <button type="button" data-testid={`remove-product-${slug(m.product)}`} onClick={() => removeProduct(i)}
              className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
          </div>
          {m.colours.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`selected-colours-${slug(m.product)}`}>
              {m.colours.map((c) => (
                <span key={c.name} className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full pl-1.5 pr-2 py-1 text-xs text-navy">
                  <Swatch hex={c.hex} px={14} />{c.name}
                  <button type="button" onClick={() => toggleColour(i, c)} className="text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="mt-2">
            <ColourMultiSelect product={m.product} selected={m.colours} onToggle={(c) => toggleColour(i, c)} />
          </div>
        </div>
      ))}

      {available.length > 0 && (
        <div className="relative">
          <button type="button" data-testid="add-product-button" onClick={() => setAddOpen((o) => !o)}
            className="flex items-center gap-1.5 text-sm font-medium text-blue-700 border border-dashed border-slate-300 rounded-lg px-3 py-2 w-full justify-center hover:bg-slate-50">
            <Plus className="w-4 h-4" /> Add sealant product
          </button>
          {addOpen && (
            <div className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
              {available.map((p) => (
                <button key={p} type="button" data-testid={`add-product-option-${slug(p)}`} onClick={() => addProduct(p)}
                  className="w-full text-left px-3 py-2.5 text-sm text-navy hover:bg-slate-50">{p}</button>
              ))}
            </div>
          )}
        </div>
      )}
      {materials.length === 0 && <p className="text-sm text-slate-400">No products added yet. Add the sealant product(s) used on site.</p>}
    </div>
  );
}
